import { useQueryClient } from '@tanstack/react-query'
import {
    SpaceAddressFromSpaceId,
    TSigner,
    useActivateOrInstallSubscription,
    useBaseWrites,
} from 'use-towns-client'
import { Address } from 'viem'

import { ensureMyAbstractAccountAddress } from './useAbstractAccountAddress'

export function useAutoRenewalSubscription({
    onSuccess,
    onError,
}: {
    onSuccess?: (args: {
        spaceAddress: Address
        tokenId: string
        myAddress: Address | undefined
        isActivate: boolean
        isMigrate: boolean
    }) => void
    onError?: (args: {
        spaceAddress: Address
        tokenId: string
        error: Error
        isActivate: boolean
        isMigrate: boolean
    }) => void
} = {}) {
    const { userOpsInstance } = useBaseWrites()
    const queryClient = useQueryClient()
    const { execute: handleSubscription, transactionStatus } = useActivateOrInstallSubscription({
        onSuccess: ({ spaceAddress, tokenId, myAddress, isActivate, isMigrate }) => {
            onSuccess?.({ spaceAddress, tokenId, myAddress, isActivate, isMigrate })
        },
        onError: ({ spaceAddress, tokenId, error, isActivate, isMigrate }) => {
            onError?.({ spaceAddress, tokenId, error, isActivate, isMigrate })
        },
    })

    const installAutoRenewal = async ({
        spaceId,
        tokenId,
        signer,
        migrate,
    }: {
        spaceId: string
        tokenId: string
        signer: TSigner
        /** Set to true to migrate an active subscription to the space's current currency */
        migrate?: boolean
    }) => {
        const signerAddress = await signer.getAddress()
        if (!signerAddress) {
            console.error(`[useAutoRenewalSubscription] no signer address found`)
            return
        }
        if (!userOpsInstance) {
            console.error(`[useAutoRenewalSubscription] no userOpsInstance found`)
            return
        }
        const walletAddress = await ensureMyAbstractAccountAddress({
            queryClient,
            rootKeyAddress: signerAddress as Address,
            userOpsInstance,
        })
        if (!walletAddress) {
            console.error('[useAutoRenewalSubscription] no walletAddress found')
            return
        }

        try {
            await handleSubscription({
                spaceAddress: SpaceAddressFromSpaceId(spaceId) as Address,
                tokenId,
                signer,
                walletAddress,
                migrate,
            })
        } catch (error: unknown) {
            console.error(`[useAutoRenewalSubscription] error handling subscription`, error)
        }
    }

    return {
        installAutoRenewal,
        transactionStatus,
    }
}
